<template>
  <div class="game-settings-container">
    <h1 class="game-title">{{ $t('PickGame.PickGameTitle') }}</h1>
    
    <div class="setting-section">
      <div class="setting-title">{{ $t('PickGame.candidatesLabel') }}</div>
      <div class="participant-counter">
        {{ participantList.length }}/20
      </div>
      <div class="vocabulary-input">
        <textarea 
          v-model="participantInput" 
          :placeholder="$t('PickGame.candidatesPlaceholder')"
          @keydown="handleKeyDown"
        ></textarea>
      </div>

      <div class="vocabulary-tags">
        <div v-for="(name, index) in participantList" :key="index" class="vocabulary-tag">
          {{ name }}
          <span class="delete-tag" @click="removeParticipant(index)">×</span>
        </div>
      </div>
    </div>

    <div class="action-buttons">
      <button class="clear-button" @click="clearData">{{ $t('PickGame.clearSettings') }}</button>
      <button class="start-button" @click="startGame" :disabled="!canStartGame">{{ $t('PickGame.saveSettings') }}</button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, inject, onMounted, getCurrentInstance } from 'vue';
import { useRouter } from 'vue-router';

const router = useRouter();
const instance = getCurrentInstance();
const { proxy } = instance;

// 通信工具類
const MessageBridge = {
    // 環境類型
    ENV: {
        WEBVIEW: 'webview',
        WEB: 'web'
    },
    // 當前環境
    currentEnv: 'webview',
    // 初始化
    init() {
        this.currentEnv = window?.chrome?.webview ? this.ENV.WEBVIEW : this.ENV.WEB;
        return this;
    },
    // 發送消息
    postMessage(data, origin = '*') {
        if (this.currentEnv === this.ENV.WEBVIEW) {
            window?.chrome?.webview.postMessage(data);
        } else {
            window.postMessage(data, origin);
        }
    },
    // 添加消息監聽器
    addEventListener(callback) {
        if (this.currentEnv === this.ENV.WEBVIEW) {
            window?.chrome?.webview.addEventListener('message', event => {
                callback(event);
            });
        } else {
            window.addEventListener('message', event => {
                callback(event);
            });
        }
    }
};

// 初始化消息橋接器
const messageBridge = MessageBridge.init();

onMounted(() => {
  messageBridge.addEventListener(event => {
    const msg = event.data;
    console.log(msg, 'msg');
  });
  
  // 從 sessionStorage 讀取配置資料
  const savedConfig = sessionStorage.getItem('PickGameConfig');
  if (savedConfig) {
    try {
      const config = JSON.parse(savedConfig);
      console.log('從 sessionStorage 讀取到的配置:', config);
      
      // 填充表單欄位 - 從 memberList 格式轉換
      if (config.memberList && Array.isArray(config.memberList)) {
        participantList.value = config.memberList.map(member => member.memberName);
      } else if (config.participantList && Array.isArray(config.participantList)) {
        // 向下相容舊格式
        participantList.value = [...config.participantList];
      }
      
    } catch (error) {
      console.error('解析 sessionStorage 中的配置時出錯:', error);
    }
  } else {
    // 載入預設數據
    loadDefaultData();
  }
});

const $Message = inject("$Message");

// 候選人列表
const participantInput = ref('');
const participantList = ref([]);

// 計算是否可以開始遊戲
const canStartGame = computed(() => participantList.value.length > 0);

// 載入預設數據
const loadDefaultData = () => {
  participantList.value = [
    'Apple', 'Banana', 'Orange', 'Grape', 'Mango',
    'Peach', 'Cherry', 'Lemon', 'Berry', 'Melon'
  ];
};

// 處理按鍵事件
const handleKeyDown = (event) => {
  // 移除紅框 (如果使用者開始編輯)
  const textarea = event.target;
  if (textarea.style.border === '2px solid red') {
    textarea.style.border = '';
  }

  // 如果按下Enter，則處理所有逗號分隔的名字
  if (event.key === 'Enter') {
    event.preventDefault();
    addParticipant();
  }
};

// 新增候選人
const addParticipant = () => {
  // 分割輸入的文本，使用中英文逗號和換行符號作為分隔符
  const names = participantInput.value.split(/[,，\n\r]+/).filter(name => name.trim() !== '');

  // 添加所有有效的名字
  for (const name of names) {
    if (participantList.value.includes(name.trim())) {
      $Message.warning({
        content: proxy.$t('PickGame.candidateExists'),
        timeout: 2000,
      });
      continue;
    }
    if (participantList.value.length < 20 && !participantList.value.includes(name.trim())) {
      participantList.value.push(name.trim());
    }
  }

  if (participantList.value.length >= 20) {
    $Message.warning({
      content: proxy.$t('PickGame.maxCandidates'),
      timeout: 2000,
    });
  }

  // 清空輸入框
  participantInput.value = '';
};

// 移除候選人
const removeParticipant = (index) => {
  participantList.value.splice(index, 1);
};

// 清空設定數據
const clearData = () => {
  participantList.value = [];
  participantInput.value = '';
  
  // 清除 sessionStorage 中的配置
  sessionStorage.removeItem('PickGameConfig');
};

// 開始遊戲
const startGame = () => {
  // 檢查輸入框是否有未按 Enter 的內容
  const hasUnsubmitted = participantInput.value.trim() !== '';

  if (hasUnsubmitted) {
    // 為輸入框添加紅框
    const textarea = document.querySelector('.vocabulary-input textarea');
    if (textarea) {
      textarea.style.border = '2px solid red';
    }

    // 顯示提示訊息
    $Message.warning({
      content: proxy.$t('PickGame.unsubmittedWarning'),
      timeout: 3000,
    });

    return; // 不執行儲存動作
  }

  // 將參與者名單轉換為 memberList 格式
  const memberList = participantList.value.map((memberName, index) => ({
    seatID: index + 1,
    memberName: memberName,
    pickedTimes: 0,
    avatar: ""
  }));

  const gameSettings = {
    pickupType: "Client",
    // isSmartPickup: false,
    memberList: memberList
  };

  console.log('遊戲設定:', gameSettings);

  // 發送設定到父組件
  messageBridge.postMessage({
    action: 'Save.PickGameSetting',
    data: gameSettings
  });

  // 導航到遊戲頁面
  router.push('/ht-pickgame-play');
};

</script>

<style lang="less" scoped>
.game-settings-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 10px 0px 20px 0px;
}

.game-title {
  font-size: 2rem;
  text-align: center;
  margin-bottom: 20px;
}

.setting-section {
  position: relative;
  margin-bottom: 10px;
  background-color: #f5f5f5;
  border-radius: 8px;
  padding: 20px;

  .setting-title {
    font-size: 1.2rem;
    margin-bottom: 10px;
  }
}

.vocabulary-input {
  textarea {
    width: 100%;
    height: 100px;
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
    resize: vertical;
    font-family: inherit;
    margin-bottom: 10px;
    font-size: 14px;
    line-height: 1.6;
  }
}

.vocabulary-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 10px;

  .vocabulary-tag {
    background-color: #e8f4ff;
    border: 1px solid #b8dcff;
    border-radius: 20px;
    padding: 5px 10px;
    display: flex;
    align-items: center;
    font-size: 0.9rem;

    .delete-tag {
      margin-left: 5px;
      cursor: pointer;
      font-weight: bold;
      color: #666;

      &:hover {
        color: #ff4d4d;
      }
    }
  }
}

.participant-counter {
  position: absolute;
  right: 22px;
  top: 25px;
  font-size: 0.9rem;
  color: #666;
  font-weight: bold;
}

.action-buttons {
  display: flex;
  justify-content: center;
  gap: 15px;

  .clear-button {
    background-color: #f44336;
    color: white;
    border: none;
    border-radius: 4px;
    padding: 5px 10px;
    font-size: 1.1rem;
    cursor: pointer;
    font-family: inherit;
    transition: background-color 0.2s;

    &:hover {
      background-color: #da190b;
    }
  }

  .start-button {
    background-color: #4caf50;
    color: white;
    border: none;
    border-radius: 4px;
    padding: 5px 10px;
    font-size: 1.1rem;
    cursor: pointer;
    font-family: inherit;
    transition: background-color 0.2s;

    &:hover {
      background-color: #45a049;
    }

    &:disabled {
      background-color: #cccccc;
      cursor: not-allowed;
    }
  }
}
</style>